import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/data-table/DataTable';
import { FormSection } from '@/components/forms/Form';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { ImportDialog, type ImportField } from '@/components/import/ImportDialog';
import {
  buildTemplate,
  TEMPLATE_FOURNISSEURS,
  TEMPLATE_TIERS_CLIENTS,
} from '@/lib/import-templates';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useAllActivites } from '@/hooks/queries/useActivites';
import {
  useBulkDeleteTiers,
  useBulkInsertTiers,
  useBulkUpdateTierActive,
  useDeleteTier,
  useTiers,
  useUpsertTier,
  type TiersFilter,
  type UpsertTierInput,
} from '@/hooks/queries/useTiers';
import { BulkActionBar, BulkButton } from '@/components/data-table/BulkActionBar';
import { BulkEditDialog, type BulkEditField } from '@/components/data-table/BulkEditDialog';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import { Download, Pencil as PencilIcon, Power, PowerOff } from 'lucide-react';
import { useRole } from '@/hooks/useRole';
import { t } from '@/locales/i18n';
import type { TierRow, TypeTier } from '@/lib/database.types';

const TYPES: { value: TypeTier; label: string }[] = [
  { value: 'client_b2b', label: 'Client B2B' },
  { value: 'client_b2c', label: 'Client B2C' },
  { value: 'fournisseur', label: 'Fournisseur' },
  { value: 'formateur', label: 'Formateur' },
  { value: 'sous_traitant', label: 'Sous-traitant' },
  { value: 'partenaire', label: 'Partenaire' },
];

const TYPE_VARIANT: Record<
  TypeTier,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline'
> = {
  client_b2b: 'default',
  client_b2c: 'secondary',
  fournisseur: 'warning',
  formateur: 'success',
  sous_traitant: 'outline',
  partenaire: 'outline',
};

const tierSchema = z.object({
  type: z.enum([
    'client_b2b',
    'client_b2c',
    'fournisseur',
    'formateur',
    'sous_traitant',
    'partenaire',
  ]),
  raison_sociale: z.string().min(2),
  code: z.string().optional().nullable(),
  nom: z.string().optional().nullable(),
  prenom: z.string().optional().nullable(),
  email: z.string().email().optional().or(z.literal('')).nullable(),
  telephone: z.string().optional().nullable(),
  ville: z.string().optional().nullable(),
  wilaya: z.string().optional().nullable(),
  pays: z.string().default('Algérie'),
  nif: z.string().optional().nullable(),
  rc: z.string().optional().nullable(),
  adresse: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  activite_principale_id: z.string().uuid().nullable().optional(),
  is_active: z.boolean().default(true),
});

type TierFormValues = z.infer<typeof tierSchema>;

export function TiersPage(): JSX.Element {
  const { isAdmin } = useRole();
  const { data: entreprises = [] } = useEntreprises();
  const { data: allActivites = [] } = useAllActivites();
  const [filter, setFilter] = useState<TiersFilter>({
    entrepriseId: null,
    type: null,
    search: '',
    active: null,
  });
  const { data: tiers = [], isLoading } = useTiers(filter);
  const upsert = useUpsertTier();
  const remove = useDeleteTier();
  const bulkInsert = useBulkInsertTiers();
  const bulkDelete = useBulkDeleteTiers();
  const bulkUpdateActive = useBulkUpdateTierActive();
  const [selectedRows, setSelectedRows] = useState<TierRow[]>([]);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const clearSelection = (): void => setSelectionVersion((v) => v + 1);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TierRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TierRow | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  // Sélection entreprise par défaut : la première (souvent KOST Academy démo)
  useEffect(() => {
    if (!filter.entrepriseId && entreprises[0]) {
      setFilter((f) => ({ ...f, entrepriseId: entreprises[0]?.id ?? null }));
    }
  }, [entreprises, filter.entrepriseId]);

  const activitesScoped = useMemo(
    () => allActivites.filter((a) => a.entreprise_id === filter.entrepriseId),
    [allActivites, filter.entrepriseId],
  );

  const columns = useMemo<ColumnDef<TierRow>[]>(
    () => [
      {
        accessorKey: 'code',
        header: 'Code',
        size: 90,
        cell: ({ getValue }) => (
          <span className="font-mono text-xs">{String(getValue() ?? '—')}</span>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        size: 130,
        cell: ({ getValue }) => {
          const v = getValue() as TypeTier;
          return (
            <Badge variant={TYPE_VARIANT[v]}>{TYPES.find((x) => x.value === v)?.label ?? v}</Badge>
          );
        },
      },
      {
        accessorKey: 'raison_sociale',
        header: 'Raison sociale / Nom',
        cell: ({ row, getValue }) => (
          <Link
            to={`/parametres/tiers/${row.original.id}`}
            className="font-medium text-primary hover:underline"
          >
            {String(getValue() ?? '—')}
          </Link>
        ),
      },
      {
        accessorKey: 'email',
        header: 'E-mail',
        size: 200,
        cell: ({ getValue }) => String(getValue() ?? '—'),
      },
      {
        accessorKey: 'telephone',
        header: 'Tél.',
        size: 130,
        cell: ({ getValue }) => String(getValue() ?? '—'),
      },
      {
        accessorKey: 'ville',
        header: 'Ville',
        size: 130,
        cell: ({ getValue }) => String(getValue() ?? '—'),
      },
      {
        accessorKey: 'is_active',
        header: 'Statut',
        size: 80,
        cell: ({ getValue }) =>
          getValue() ? (
            <Badge variant="success">Actif</Badge>
          ) : (
            <Badge variant="outline">Inactif</Badge>
          ),
      },
      {
        id: 'actions',
        header: '',
        size: 80,
        enableSorting: false,
        cell: ({ row }) =>
          isAdmin ? (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => {
                  setEditing(row.original);
                  setDialogOpen(true);
                }}
                aria-label="Modifier"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => setConfirmDelete(row.original)}
                aria-label="Supprimer"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : null,
      },
    ],
    [isAdmin],
  );

  const importFields: ImportField[] = useMemo(
    () => [
      { key: 'raison_sociale', label: 'Raison sociale', required: true },
      { key: 'code', label: 'Code interne' },
      { key: 'nom', label: 'Nom' },
      { key: 'prenom', label: 'Prénom' },
      { key: 'email', label: 'E-mail' },
      { key: 'telephone', label: 'Téléphone' },
      { key: 'ville', label: 'Ville' },
      { key: 'wilaya', label: 'Wilaya' },
      { key: 'nif', label: 'NIF' },
      { key: 'rc', label: 'RC' },
      { key: 'adresse', label: 'Adresse' },
    ],
    [],
  );

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('parametres.tiers.title')}
        subtitle={t('parametres.tiers.subtitle')}
        actions={
          isAdmin && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <Upload className="h-4 w-4" />
                {t('parametres.tiers.import')}
              </Button>
              <Button
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
                {t('parametres.tiers.add')}
              </Button>
            </div>
          )
        }
      />

      <div className="space-y-4 p-6">
        <div className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-card p-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {t('parametres.tiers.filters.entreprise')}
            </label>
            <select
              value={filter.entrepriseId ?? ''}
              onChange={(e) => setFilter((f) => ({ ...f, entrepriseId: e.target.value || null }))}
              className="h-9 w-56 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">— Toutes —</option>
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {t('parametres.tiers.filters.type')}
            </label>
            <select
              value={filter.type ?? ''}
              onChange={(e) =>
                setFilter((f) => ({ ...f, type: (e.target.value || null) as TypeTier | null }))
              }
              className="h-9 w-44 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">— Tous —</option>
              {TYPES.map((t2) => (
                <option key={t2.value} value={t2.value}>
                  {t2.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {t('parametres.tiers.filters.activite')}
            </label>
            <select
              value={filter.activiteId ?? ''}
              onChange={(e) => setFilter((f) => ({ ...f, activiteId: e.target.value || null }))}
              className="h-9 w-56 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">— Toutes —</option>
              {activitesScoped.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nom}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {t('parametres.tiers.filters.status')}
            </label>
            <select
              value={
                filter.active === null || filter.active === undefined ? '' : String(filter.active)
              }
              onChange={(e) => {
                const v = e.target.value;
                setFilter((f) => ({ ...f, active: v === '' ? null : v === 'true' }));
              }}
              className="h-9 w-32 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">— Tous —</option>
              <option value="true">Actifs</option>
              <option value="false">Inactifs</option>
            </select>
          </div>
        </div>

        <DataTable
          key={selectionVersion}
          data={tiers}
          columns={columns}
          loading={isLoading}
          pageSize={20}
          searchPlaceholder="Rechercher dans les tiers (raison sociale, e-mail, téléphone…)"
          exportFileName="tiers.csv"
          enableSelection={isAdmin}
          getRowId={(row) => row.id}
          onSelectionChange={setSelectedRows}
          bulkBar={
            <BulkActionBar count={selectedRows.length} onClear={clearSelection}>
              <BulkButton onClick={() => exportTiersCSV(selectedRows)}>
                <Download className="h-3.5 w-3.5" /> Exporter CSV
              </BulkButton>
              <BulkButton
                onClick={async () => {
                  await bulkUpdateActive.mutateAsync({
                    ids: selectedRows.map((r) => r.id),
                    is_active: true,
                  });
                  toast.success(`${selectedRows.length} tier(s) activé(s)`);
                  clearSelection();
                }}
              >
                <Power className="h-3.5 w-3.5" /> Activer
              </BulkButton>
              <BulkButton
                onClick={async () => {
                  await bulkUpdateActive.mutateAsync({
                    ids: selectedRows.map((r) => r.id),
                    is_active: false,
                  });
                  toast.success(`${selectedRows.length} tier(s) désactivé(s)`);
                  clearSelection();
                }}
              >
                <PowerOff className="h-3.5 w-3.5" /> Désactiver
              </BulkButton>
              <BulkButton onClick={() => setBulkEditOpen(true)}>
                <PencilIcon className="h-3.5 w-3.5" /> Modifier en masse
              </BulkButton>
              <BulkButton variant="destructive" onClick={() => setBulkConfirmDelete(true)}>
                <Trash2 className="h-3.5 w-3.5" /> Supprimer
              </BulkButton>
            </BulkActionBar>
          }
        />
      </div>

      <TierFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        entrepriseId={filter.entrepriseId ?? entreprises[0]?.id ?? ''}
        activitesScoped={activitesScoped}
        onSubmit={async (values) => {
          await upsert.mutateAsync({
            id: editing?.id,
            entreprise_id: filter.entrepriseId ?? entreprises[0]?.id ?? '',
            ...values,
          } as UpsertTierInput);
          toast.success(editing ? 'Tiers mis à jour' : 'Tiers créé');
          setDialogOpen(false);
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer « ${confirmDelete?.raison_sociale} » ?`}
        loading={remove.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await remove.mutateAsync(confirmDelete.id);
          toast.success('Tiers supprimé');
          setConfirmDelete(null);
        }}
      />

      <DangerousConfirmDialog
        open={bulkConfirmDelete}
        onOpenChange={setBulkConfirmDelete}
        title={`Supprimer ${selectedRows.length} tier${selectedRows.length > 1 ? 's' : ''} ?`}
        description="Action irréversible. Les opérations financières liées garderont leur référence (set null)."
        consequences={[
          `${selectedRows.length} ligne(s) seront définitivement effacées`,
          'Les contacts liés (tiers_contacts) seront supprimés en cascade',
          "L'action est journalisée dans audit_log",
        ]}
        challenge="SUPPRIMER"
        loading={bulkDelete.isPending}
        onConfirm={async () => {
          const r = await bulkDelete.mutateAsync(selectedRows.map((x) => x.id));
          if (r.errors.length === 0) toast.success(`${r.success.length} tier(s) supprimé(s)`);
          else toast.warning(`${r.success.length} OK · ${r.errors.length} échec(s)`);
          setBulkConfirmDelete(false);
          clearSelection();
        }}
      />

      <BulkEditDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        table="tiers"
        ids={selectedRows.map((r) => r.id)}
        fields={
          [
            {
              key: 'activite_principale_id',
              label: 'Activité principale',
              type: 'select',
              options: [
                { value: '', label: '— Aucune —' },
                ...activitesScoped.map((a) => ({ value: a.id, label: a.nom })),
              ],
            },
            {
              key: 'is_active',
              label: 'Statut',
              type: 'select',
              options: [
                { value: 'true', label: 'Actif' },
                { value: 'false', label: 'Inactif' },
              ],
            },
            { key: 'ville', label: 'Ville', type: 'text' },
            { key: 'wilaya', label: 'Wilaya', type: 'text' },
          ] satisfies BulkEditField[]
        }
        onApplied={() => clearSelection()}
      />

      <ImportDialog<UpsertTierInput>
        open={importOpen}
        onOpenChange={setImportOpen}
        title={
          filter.type === 'fournisseur'
            ? 'Importer des fournisseurs (XLSX/CSV)'
            : 'Importer des clients B2B/B2C (XLSX/CSV)'
        }
        fields={importFields}
        generateTemplate={() =>
          buildTemplate(
            filter.type === 'fournisseur' ? TEMPLATE_FOURNISSEURS : TEMPLATE_TIERS_CLIENTS,
          )
        }
        templateFilename={
          filter.type === 'fournisseur'
            ? TEMPLATE_FOURNISSEURS.filename
            : TEMPLATE_TIERS_CLIENTS.filename
        }
        onImport={async (rows) => {
          const entreprise_id = filter.entrepriseId ?? entreprises[0]?.id;
          if (!entreprise_id) {
            return { success: 0, errors: ['Sélectionnez une entreprise avant import.'] };
          }
          const type: TypeTier = filter.type ?? 'client_b2b';
          const errors: string[] = [];
          const valid: UpsertTierInput[] = [];
          rows.forEach((r, i) => {
            if (!r.raison_sociale) {
              errors.push(`Ligne ${i + 2} : raison sociale manquante`);
              return;
            }
            valid.push({ ...r, entreprise_id, type } as UpsertTierInput);
          });
          if (valid.length === 0) return { success: 0, errors };
          try {
            await bulkInsert.mutateAsync(valid);
            return { success: valid.length, errors };
          } catch (err) {
            return { success: 0, errors: [...errors, (err as Error).message] };
          }
        }}
      />
    </div>
  );
}

function TierFormDialog({
  open,
  onOpenChange,
  editing,
  entrepriseId,
  activitesScoped,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: TierRow | null;
  entrepriseId: string;
  activitesScoped: { id: string; nom: string }[];
  onSubmit: (values: TierFormValues) => Promise<void>;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<TierFormValues>({
    resolver: zodResolver(tierSchema),
    defaultValues: { type: 'client_b2b', raison_sociale: '', pays: 'Algérie', is_active: true },
  });

  useEffect(() => {
    if (open) {
      reset(
        editing
          ? {
              type: editing.type,
              raison_sociale: editing.raison_sociale,
              code: editing.code,
              nom: editing.nom,
              prenom: editing.prenom,
              email: editing.email,
              telephone: editing.telephone,
              ville: editing.ville,
              wilaya: editing.wilaya,
              pays: editing.pays,
              nif: editing.nif,
              rc: editing.rc,
              adresse: editing.adresse,
              notes: editing.notes,
              activite_principale_id: editing.activite_principale_id,
              is_active: editing.is_active,
            }
          : { type: 'client_b2b', raison_sociale: '', pays: 'Algérie', is_active: true },
      );
    }
  }, [open, editing, reset]);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={editing ? t('parametres.tiers.edit') : t('parametres.tiers.add')}
      description="Entreprise : sélection courante des filtres."
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(async (v) => {
        if (!entrepriseId) {
          toast.error("Sélectionnez d'abord une entreprise dans les filtres.");
          return;
        }
        await onSubmit(v);
      })}
    >
      <FormSection title="Identité">
        <FormSelect
          label="Type"
          required
          value={watch('type')}
          onChange={(v) => setValue('type', v as TypeTier, { shouldValidate: true })}
          options={TYPES}
        />
        <FormField label="Code interne" {...register('code')} hint="Optionnel" />
        <FormField
          label="Raison sociale / Nom complet"
          required
          {...register('raison_sociale')}
          error={errors.raison_sociale?.message}
          fullWidth
        />
        <FormField label="Nom" {...register('nom')} />
        <FormField label="Prénom" {...register('prenom')} />
        <FormField label="NIF" {...register('nif')} />
        <FormField label="RC" {...register('rc')} />
      </FormSection>

      <FormSection title="Contact & adresse">
        <FormField
          type="email"
          label="E-mail"
          {...register('email')}
          error={errors.email?.message}
        />
        <FormField label="Téléphone" {...register('telephone')} placeholder="0555-00-00-00" />
        <FormField label="Ville" {...register('ville')} />
        <FormField label="Wilaya" {...register('wilaya')} />
        <FormField label="Pays" {...register('pays')} />
        <FormSelect
          label="Activité principale"
          value={watch('activite_principale_id') ?? ''}
          onChange={(v) => setValue('activite_principale_id', v || null)}
          options={[
            { value: '', label: '—' },
            ...activitesScoped.map((a) => ({ value: a.id, label: a.nom })),
          ]}
        />
        <FormTextarea label="Adresse" {...register('adresse')} fullWidth />
      </FormSection>

      <FormSection title="Notes">
        <FormTextarea label="Notes internes" {...register('notes')} fullWidth />
      </FormSection>
    </FormDialog>
  );
}

function exportTiersCSV(rows: TierRow[]): void {
  if (rows.length === 0) return;
  const headers = [
    'code',
    'type',
    'raison_sociale',
    'nom',
    'prenom',
    'email',
    'telephone',
    'ville',
    'wilaya',
    'pays',
    'nif',
    'rc',
    'is_active',
  ];
  const csvRows = rows.map((r) =>
    headers
      .map((h) => {
        const v = (r as unknown as Record<string, unknown>)[h];
        const s = v == null ? '' : String(v);
        return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      })
      .join(','),
  );
  const csv = [headers.join(','), ...csvRows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tiers-${rows.length}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success(`${rows.length} tier(s) exporté(s)`);
}
